import { useCallback } from "react";
import { atom, selector, useRecoilValue, useSetRecoilState } from "recoil";

import { UserDTO } from "api";

export const USER_ROLES = {
  ADMIN: "admin",
  AUDITOR: "AUDITOR",
  SITE_MANAGER: "site_manager",
  DATA_VALIDATOR: "DATA_VALIDATOR",
  ENABLED: "enabled",
  DATA_PROVIDER: "DATA_PROVIDER",
  MODULE_ADMIN: "MODULE_ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  DATA_REVIEWER: "DATA_REVIEWER",
  DATA_CONTROLLER: "DATA_CONTROLLER",
  FINAL_APPROVER: "FINAL_APPROVER",
  SUSTAINABILITY_MANAGER: "SUSTAINABILITY_MANAGER",
} as const;

export const USER_ROLES_DROPDOWN = {
  AUDITOR: "AUDITOR",
  DATA_PROVIDER: "DATA_PROVIDER",
  DATA_VALIDATOR: "DATA_VALIDATOR",
  MODULE_ADMIN: "MODULE_ADMIN",
} as const;

export const USER_ROLES_DROPDOWN_APPROVAL_FLOW = {
  AUDITOR: "AUDITOR",
  DATA_PROVIDER: "DATA_PROVIDER",
  MODULE_ADMIN: "MODULE_ADMIN",
  DATA_VALIDATOR: "DATA_VALIDATOR",
  DATA_REVIEWER: "DATA_REVIEWER",
} as const;

export const USER_ROLES_NAMES: any = {
  [USER_ROLES.ADMIN]: "Sustainability manager",
  [USER_ROLES.AUDITOR]: "Auditor",
  [USER_ROLES.SITE_MANAGER]: "Data Provider",
  [USER_ROLES.DATA_VALIDATOR]: "Data Validator",
  [USER_ROLES.ENABLED]: "Enabled",
  [USER_ROLES.DATA_PROVIDER]: "Data Provider",
  [USER_ROLES.MODULE_ADMIN]: "Module Admin",
  [USER_ROLES.DATA_REVIEWER]: "Data Reviewer",
};

export const USER_ROLES_NAMES_DROPDOWN: any = {
  [USER_ROLES.AUDITOR]: "Auditor",
  [USER_ROLES.DATA_PROVIDER]: "Data Provider",
  [USER_ROLES.DATA_REVIEWER]: "Data Reviewer",
  [USER_ROLES.DATA_VALIDATOR]: "Data Validator",
  [USER_ROLES.MODULE_ADMIN]: "Module Admin",
};

export const USER_ROLES_NAME_TO_VALUE = Object.fromEntries(
  Object.entries(USER_ROLES_NAMES).map(([key, value]) => [value, key]),
);

export const USER_ROLE_COLOR = {
  [USER_ROLES.ADMIN]: "#E7C69A", // Sustainability manager
  [USER_ROLES.AUDITOR]: "#86B2A3", // auditor
  [USER_ROLES.SITE_MANAGER]: "#AAF0D5", // data provider
  [USER_ROLES.ENABLED]: "#C8E1F7", // Disabled
};

export const getUserRoleObject = (role: UserRole) => {
  if (role === USER_ROLES.ADMIN) {
    return {
      admin: true,
      auditor: false,
      enabled: true,
    };
  }

  if (role === USER_ROLES.AUDITOR) {
    return {
      admin: false,
      auditor: true,
      enabled: true,
    };
  }

  if (role === USER_ROLES.ENABLED) {
    return {
      enabled: false,
      admin: false,
      auditor: false,
    };
  }

  return {
    admin: false,
    auditor: false,
  };
};

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

interface UnauthenticatedUser {
  isAuthenticated: false;
}

export interface AuthenticatedUser extends UserDTO {
  isAuthenticated: true;
}

export type UserState = UnauthenticatedUser | AuthenticatedUser;

const DEFAULT_USER_STATE: UnauthenticatedUser = {
  isAuthenticated: false,
};

export const userState = atom<UserState>({
  key: "user",
  default: DEFAULT_USER_STATE,
});

export const isUserAuthenticatedState = selector({
  key: "isUserAuthenticated",
  get: ({ get }) => get(userState).isAuthenticated,
});

export const useSetDefaultUser = () => {
  const setUserState = useSetRecoilState(userState);
  return useCallback(() => setUserState(DEFAULT_USER_STATE), [setUserState]);
};

export const useSetUser = () => {
  const setUserState = useSetRecoilState(userState);

  return useCallback(
    (user?: UserDTO) => {
      if (!user) {
        setUserState(DEFAULT_USER_STATE);
        throw Error("Can not set authenticated user.");
      }

      setUserState({
        isAuthenticated: true,
        ...user,
      });
    },
    [setUserState],
  );
};


export const useAuthenticatedUserState = (): AuthenticatedUser => {
  const user = useRecoilValue(userState);

  if (!user.isAuthenticated) {
    throw new Error(`The user must be authenticated!`);
  }
  return user;
};

export const useUserRole = () => {
  const { roles } = useAuthenticatedUserState();

  const isSuperAdmin = roles.includes(USER_ROLES.SUPER_ADMIN);
  const isAdmin = roles.includes(USER_ROLES.MODULE_ADMIN);
  const isAuditor = roles.includes(USER_ROLES.AUDITOR);

  const isDataProvider = !isAdmin && !isAuditor;

  const role = getUserRole(roles);

  const isRoleIn = useCallback(
    (checkRoles: UserRole[]) => checkRoles.some(r => roles.includes(r)),
    [roles],
  );

  return {
    isAdmin,
    isAuditor,
    isDataProvider,
    isSuperAdmin,
    role,
    isRoleIn,
  };
};

export const useGetSitesAllowedByUser = () => {
  const { sites } = useAuthenticatedUserState();

  return sites;
};

export const getUserRole = (roles: string[]): UserRole[] => {
  const availableRoles: UserRole[] = [
    USER_ROLES.ADMIN,
    USER_ROLES.AUDITOR,
    USER_ROLES.DATA_CONTROLLER,
    USER_ROLES.DATA_PROVIDER,
    USER_ROLES.FINAL_APPROVER,
    USER_ROLES.SUSTAINABILITY_MANAGER,
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.MODULE_ADMIN,
  ];

  const matchedRoles = availableRoles.filter(role => roles.includes(role));
  return matchedRoles.length ? matchedRoles : [USER_ROLES.AUDITOR]
};


